import React from "react";
import { formatDistanceToNow } from "date-fns";
import { 
  Package, 
  Plane, 
  Clock, 
  Check, 
  X, 
  MessageSquare,
  ArrowRight
} from "lucide-react";

const statusConfig = {
  pending: { 
    color: "bg-amber-100 text-amber-800 border-amber-200", 
    icon: Clock,
    label: "Awaiting Response" 
  },
  countered: { 
    color: "bg-blue-100 text-blue-800 border-blue-200", 
    icon: MessageSquare,
    label: "Counter Offered" 
  },
  accepted: { 
    color: "bg-green-100 text-green-800 border-green-200", 
    icon: Check,
    label: "Accepted" 
  },
  rejected: { 
    color: "bg-red-100 text-red-800 border-red-200", 
    icon: X,
    label: "Declined" 
  },
  expired: { 
    color: "bg-gray-100 text-gray-800 border-gray-200", 
    icon: Clock,
    label: "Expired" 
  }
};

/**
 * Card component for displaying a negotiation in a list
 */
export function NegotiationCard({ negotiation, currentUserId, onClick }) {
  const isUser = negotiation.user_id === currentUserId;
  const isMyTurn = (isUser && negotiation.whose_turn === "user") || 
                   (!isUser && negotiation.whose_turn === "partner");
  const status = statusConfig[negotiation.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  const discount = ((negotiation.original_price - negotiation.current_offer) / negotiation.original_price * 100).toFixed(0);

  return (
    <div 
      onClick={onClick}
      className={`border border-border p-4 cursor-pointer hover:border-orange-300 transition-colors ${
        isMyTurn && ["pending", "countered"].includes(negotiation.status) 
          ? "border-l-4 border-l-orange-500" 
          : ""
      }`}
      data-testid={`negotiation-card-${negotiation.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left - Listing Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {negotiation.listing_type === "cargo" ? (
              <Package className="w-4 h-4 text-muted-foreground" />
            ) : (
              <Plane className="w-4 h-4 text-muted-foreground" />
            )}
            <span className="font-medium truncate">{negotiation.listing_title}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>With: {isUser ? negotiation.partner_name : negotiation.user_name}</span>
          </div>

          <div className="flex items-center gap-3 mt-2 text-sm">
            <span className="text-muted-foreground">
              ₹{negotiation.original_price}
            </span>
            <ArrowRight className="w-3 h-3" />
            <span className="font-bold text-orange-600">
              ₹{negotiation.current_offer}
            </span>
            {discount > 0 && (
              <span className="text-xs text-green-600">(-{discount}%)</span>
            )}
          </div>
        </div>

        {/* Right - Status */}
        <div className="text-right">
          <div className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${status.color}`}>
            <StatusIcon className="w-3 h-3" />
            {status.label}
          </div>
          
          {isMyTurn && ["pending", "countered"].includes(negotiation.status) && (
            <p className="text-xs text-orange-600 font-medium mt-1">Your turn!</p>
          )}
          
          <p className="text-xs text-muted-foreground mt-1">
            Round {negotiation.current_round}/{negotiation.max_rounds}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center mt-3 pt-2 border-t border-border text-xs text-muted-foreground">
        <span>
          Updated {formatDistanceToNow(new Date(negotiation.updated_at), { addSuffix: true })}
        </span>
        {["pending", "countered"].includes(negotiation.status) && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Expires {formatDistanceToNow(new Date(negotiation.expires_at), { addSuffix: true })}
          </span>
        )}
      </div>
    </div>
  );
}
